import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { closeModal } from '../../actions/ModalActions';
import Modal from '../Modal';
import discountApi from './discountApi';
import { benefitWindow } from '../../utils/discountFormat';

/**
 * Apply one promotion to a cohort (OPE-168, roadmap 3.7) — the marketing ask
 * "20% off for six months for NZ signups converted in July", or a pasted
 * list of Fergus ids.
 *
 * Always dry first: the run comes back as a per-client table and only the
 * second call, from the button under it, writes anything. The form is
 * frozen while a request is in flight and every edit invalidates any
 * response still on its way, so Apply can never submit a cohort that was
 * not the one previewed.
 */
@connect(
    () => ({}),
    dispatch => bindActionCreators({ closeModal }, dispatch)
)
export default class BulkApplyModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isRunning: false,
            error: null,
            result: null,
            applied: false,
            form: {
                mode: 'window',
                country: (props.promotionCountries || [])[0] || ((props.countries || [])[0] || {}).code || 'NZ',
                converted_after: '',
                converted_before: '',
                fergus_ids: '',
                anchor_type: 'application_date',
                starts_on: '',
                reason: ''
            }
        };
    }

    set(field, value) {
        // Any change invalidates the dry run shown below the form, including
        // one still in flight (its response is discarded in run()).
        this.requestSeq = (this.requestSeq || 0) + 1;
        this.setState({ form: { ...this.state.form, [field]: value }, result: null, applied: false });
    }

    body(dryRun) {
        var f = this.state.form;
        var body = { dry_run: dryRun, anchor_type: f.anchor_type, reason: f.reason || null };
        if (f.anchor_type === 'explicit') {
            body.starts_on = f.starts_on;
        }
        if (f.mode === 'window') {
            body.country = f.country;
            body.converted_after = f.converted_after;
            body.converted_before = f.converted_before;
        } else {
            body.fergus_ids = f.fergus_ids.split(/[\s,]+/).filter(id => id);
        }

        return body;
    }

    run(dryRun) {
        var seq = this.requestSeq = (this.requestSeq || 0) + 1;
        this.setState({ isRunning: true, error: null });
        var request = this.props.promotion
            ? discountApi.bulkApplyPromotion(this.props.promotion.promotion_id, this.body(dryRun))
            : discountApi.bulkApplyCoupon(this.props.coupon.coupon_id, this.body(dryRun));
        request
            .then(result => {
                if (seq !== this.requestSeq) {
                    return; // the form changed underneath this request
                }
                this.setState({ isRunning: false, result, applied: !dryRun });
                if (!dryRun && this.props.onDone) {
                    this.props.onDone();
                }
            })
            .catch(error => {
                if (seq === this.requestSeq) {
                    this.setState({ isRunning: false, error: error.message });
                }
            });
    }

    isComplete() {
        var f = this.state.form;
        if (f.anchor_type === 'explicit' && !f.starts_on) {
            return false;
        }
        return f.mode === 'window'
            ? !!(f.country && f.converted_after && f.converted_before)
            : this.body(true).fergus_ids.length > 0;
    }

    render() {
        var { form, isRunning, error, result, applied } = this.state;
        var promotion = this.props.promotion;
        var title = promotion
            ? `Bulk apply: ${promotion.name}${promotion.coupon ? ' — ' + promotion.coupon.invoice_name : ''}`
            : `Bulk apply coupon: ${this.props.coupon.invoice_name} (no country or eligibility check)`;
        var summary = result ? result.summary : null;
        var willApply = summary ? summary.applied + summary.pending_activation : 0;
        var canRun = this.isComplete() && !isRunning;
        var canApply = willApply > 0 && !isRunning;
        var actionButton;
        if (applied) {
            actionButton = <button className="btn btn-success disabled">Applied</button>;
        } else if (result) {
            actionButton = (
                <button className={`btn btn-success ${canApply ? '' : 'disabled'}`} onClick={canApply ? () => this.run(false) : undefined}>
                    {isRunning ? 'Applying...' : `Apply to ${willApply} client${willApply === 1 ? '' : 's'}`}
                </button>
            );
        } else {
            actionButton = (
                <button className={`btn btn-info ${canRun ? '' : 'disabled'}`} onClick={canRun ? () => this.run(true) : undefined}>
                    {isRunning ? 'Running...' : 'Preview'}
                </button>
            );
        }

        return (
            <Modal actionButton={actionButton} className="modal-primary" title={title}>
                {error && <div className="alert alert-danger">{error}</div>}
                {applied &&
                    <div className="alert alert-success">
                        Applied to {summary.applied + summary.pending_activation} client(s). Each one got a benefit window, an audit event and a Fergus sync.
                    </div>
                }

                <fieldset disabled={isRunning}>
                <div className="form-group">
                    <label className="radio-inline">
                        <input type="radio" name="mode" checked={form.mode === 'window'} onChange={() => this.set('mode', 'window')} /> Country + converted window
                    </label>
                    <label className="radio-inline">
                        <input type="radio" name="mode" checked={form.mode === 'fergus_ids'} onChange={() => this.set('mode', 'fergus_ids')} /> Paste Fergus IDs
                    </label>
                </div>

                {form.mode === 'window' &&
                    <div className="row">
                        <div className="col-xs-4">
                            <div className="form-group">
                                <label>Country</label>
                                <select className="form-control" value={form.country} onChange={e => this.set('country', e.target.value)}>
                                    {(this.props.countries || []).map(c => <option key={c.code} value={c.code}>{c.code} – {c.name}</option>)}
                                </select>
                                <small className="text-muted">Matched on the client's billing region.</small>
                            </div>
                        </div>
                        <div className="col-xs-4">
                            <div className="form-group">
                                <label>Converted from</label>
                                <input type="date" className="form-control" value={form.converted_after} onChange={e => this.set('converted_after', e.target.value)} />
                            </div>
                        </div>
                        <div className="col-xs-4">
                            <div className="form-group">
                                <label>Converted to <small className="text-muted">inclusive</small></label>
                                <input type="date" className="form-control" value={form.converted_before} onChange={e => this.set('converted_before', e.target.value)} />
                            </div>
                        </div>
                    </div>
                }

                {form.mode === 'fergus_ids' &&
                    <div className="form-group">
                        <label>Fergus IDs</label>
                        <textarea className="form-control" rows={4} value={form.fergus_ids} placeholder="One per line, or comma separated" onChange={e => this.set('fergus_ids', e.target.value)} />
                        <small className="text-muted">Anything that matches no client is listed back to you and nothing is applied for it.</small>
                    </div>
                }

                <div className="row">
                    <div className="col-xs-4">
                        <div className="form-group">
                            <label>Discount starts</label>
                            <select className="form-control" value={form.anchor_type} onChange={e => this.set('anchor_type', e.target.value)}>
                                <option value="application_date">Today</option>
                                <option value="latest_activation">At their latest paid activation</option>
                                <option value="explicit">On a date I pick</option>
                            </select>
                        </div>
                    </div>
                    {form.anchor_type === 'explicit' &&
                        <div className="col-xs-4">
                            <div className="form-group">
                                <label>Start date</label>
                                <input type="date" className="form-control" value={form.starts_on} onChange={e => this.set('starts_on', e.target.value)} />
                            </div>
                        </div>
                    }
                    <div className="col-xs-4">
                        <div className="form-group">
                            <label>Reason <small className="text-muted">on every audit event</small></label>
                            <input className="form-control" maxLength={200} value={form.reason} placeholder="e.g. July NZ signup campaign" onChange={e => this.set('reason', e.target.value)} />
                        </div>
                    </div>
                </div>
                </fieldset>

                {result &&
                    <div>
                        <p style={{ marginBottom: 5 }}>
                            <strong>{summary.matched}</strong> matched · <strong>{willApply}</strong> {applied ? 'applied' : 'would get it'} · <strong>{summary.skipped}</strong> skipped · <strong>{summary.errors}</strong> error(s)
                        </p>
                        {(result.missing_ids || []).length > 0 &&
                            <div className="alert alert-warning" style={{ padding: 8 }}>
                                No client for: {result.missing_ids.join(', ')}
                            </div>
                        }
                        {summary.matched === 0 && <p className="text-muted">Nothing matched that selection.</p>}
                        {result.rows.length > 0 &&
                            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                <table className="table table-condensed" style={{ marginBottom: 0 }}>
                                    <thead><tr><th>Client</th><th>Fergus ID</th><th>Outcome</th><th>Window</th></tr></thead>
                                    <tbody>
                                        {result.rows.map(row =>
                                            <tr key={row.client_id} className={row.reason ? 'text-muted' : ''}>
                                                <td>{row.client_name}</td>
                                                <td><small>{row.fergus_id || '–'}</small></td>
                                                <td>{row.reason ? `${row.outcome}: ${row.reason}` : row.outcome}</td>
                                                <td>{row.starts_on ? benefitWindow(row.starts_on, row.ends_on) : '–'}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        }
                        {!applied && <p className="text-muted" style={{ marginTop: 8 }}><small>Nothing has been written yet.</small></p>}
                    </div>
                }
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./app/components/discounts/BulkApplyModal.js